import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useForm } from "react-hook-form"
import moment from "moment"
import ESMScreen, { getType } from "../../screen/ESMScreen"
import { useUsers } from "../../core/utils/hooks/useUsers"
import { useNavigate, useParams } from "react-router"
import { useScope } from "../SelectProjectCompanyContainer"
import { setData } from "../../core/redux/app/Actions"
import { Button } from "../../components/Button"
import Send from "../../core/communication"
import { PageHeaderAddon } from "../../components/Header"
import { BadgeEsm } from "../../components/Badge"
import { isJson } from "../../core/utils/jsonVerify"
import { customToast } from "../../components/Toast"
import { applyFilterEsm } from "../../core/utils/farmerFilter/filters"
import { evolutionFilterTree } from "../../core/utils/evolutionFilterMock"
import { sort } from "fast-sort"
import { jsonToExcel } from "../../core/utils/jsonToExcel"


const variationsColors = [
  '#dff4f0',
  '#3b538e',
  '#8399e9',
  '#32291d',
  '#c4b9d6',
  '#643466',
  '#3b23d9',
  '#3db6f5',
  '#ce7070',
  '#a12070',
  '#b7df9d',
  '#514119',
  '#797e59',
  '#f5e5ad',
  '#d3beea',
  '#0fe9a7',
  '#373504',
  '#52760F',
  '#2496a7',
  '#ad78ce',
  '#4b3c8e',
  '#9fb4ac',
  '#a6e990',
  '#a9d5a6',
];

const toJson = (content, defaultData = {}) => {
  try {
    return JSON.parse(content)
  } catch (e) {
    console.log({ e })
    return defaultData
  }
}

export const calculateAccumulatedAverages = (data, key = "summary") => {
  const monthlySummary = {};

  // Agrupa os dados por usuário e mês, mantendo apenas o mais recente para cada mês
  const filteredData = {};

  data.forEach(item => {
    const monthYear = item.details.monthYear;
    const userId = item.userId;
    const date = item.date;  // Supondo que haja um campo date com o formato DD/MM/YYYY

    const key = `${userId}-${monthYear}`;
    
    // Mantém apenas a linha com a data mais recente para o mesmo usuário/mês
    if (!filteredData[key] || moment(date, "DD/MM/YYYY").isAfter(moment(filteredData[key].date, "DD/MM/YYYY"))) {
      filteredData[key] = item;
    }
  });

  // Itera sobre os dados filtrados e acumula por mês
  Object.values(filteredData).forEach(item => {
    const monthYear = item.details.monthYear;
    const fields = item.fields;
    const summary = item[key];
    const userId = item.userId;

    if (!monthlySummary[monthYear]) {
      monthlySummary[monthYear] = { users: new Set(), details: item.details };
    }

    // Adiciona o userId para rastrear produtores únicos
    monthlySummary[monthYear].users.add(userId);

    const verifyEntries = (summary) => {
      Object.entries(summary).forEach(([key, value]) => {
        if (!monthlySummary[monthYear][key]) {
          monthlySummary[monthYear][key] = { total: 0, count: 0 };
        }
  
        if (value !== null && !isNaN(value)) {
          monthlySummary[monthYear][key].total += value;
          monthlySummary[monthYear][key].count += 1;
        }
      });
    };

    if (key === "fields") {
      let summary = {};
      for (let field of fields) {
        summary = {
          ...summary,
          [`${field.category}-${field.title}`]: field.value 
        };
      }
      verifyEntries(summary);
    } else {
      verifyEntries(summary);
    }
  });

  // Ordena os meses de forma crescente
  const sortedEntries = Object.entries(monthlySummary).sort(([a], [b]) => {
    const [monthA, yearA] = a.split('/').map(Number);
    const [monthB, yearB] = b.split('/').map(Number);

    return yearA !== yearB ? yearA - yearB : monthA - monthB;
  });

  // Calcula o acumulado progressivo para cada mês
  let accumulated = {};
  let accumulatedUsers = new Set();  // Para acumular produtores únicos ao longo do tempo

  const accumulatedByMonth = sortedEntries.map(([monthYear, fields]) => {
    const accumulatedAverages = {};

    // Acumula os produtores de meses anteriores
    fields.users.forEach(user => accumulatedUsers.add(user));
    const numberOfProducers = accumulatedUsers.size;  // Contagem de produtores únicos acumulados

    Object.entries(fields).forEach(([field, { total, count }]) => {
      if (field === 'users' || field === 'details') return;  // Pula a contagem de usuários e detalhes no loop dos campos

      if (!accumulated[field]) {
        accumulated[field] = { total: 0, count: 0 };
      }

      accumulated[field].total += total;
      accumulated[field].count += count;

      accumulatedAverages[field] = accumulated[field].count > 0
        ? parseFloat((accumulated[field].total / accumulated[field].count).toFixed(2))
        : 0;
    });

    return {
      monthYear,
      "Geral": +(!isNaN(fields?.details?.Geral) && parseFloat(fields?.details?.Geral || 0).toFixed(2) || 0),  // Inclui a pontuação geral no retorno
      ...accumulatedAverages,
      "N.º de Produtores": numberOfProducers  // Adiciona o número de produtores acumulados
    };
  });

  return accumulatedByMonth;
};


export const formatEsmData = (data, users) => {
  let dataToReturn = data.map((item) => ({
    scaleProducerId: item?.scaleProducerId || '',
    userId: item.userId,
    projectId: item?.projectId,
    user: users?.filter(user => user.id === item.userId) ?? [],
    date: moment(item.date).format("DD/MM/YYYY"),
    year: moment(item.date).format("YYYY"),
    details: {
      Conceito: item.concept,
      Geral: item.generalScore,
      Ecologico: item.ecologicalScore,
      Humano: item.humanScore,
      monthYear: moment(item?.date).format('MM/YYYY')
    },
    summary: toJson(item.summary),
    fields: toJson(item.fields, []),
    codes: item.codes || []
  }));

  let colorsByMonthYear = {};

  const onlyDate = [...new Set(dataToReturn.map(item => item.details.monthYear))];

  // Atribui cores por mês/ano
  onlyDate.map((item, index) => {
    let currentColor = index;
    if (index >= 24) {
      currentColor = parseInt(Math.random() * (23 - 0) + 0);
    }
    colorsByMonthYear = {
      ...colorsByMonthYear,
      [item]: variationsColors[currentColor]
    };
  });

  // Calcula a variação por usuário e projeto
  const variationByUserProject = {};

  // Agrupar por usuário/projeto
  const groupedData = dataToReturn.reduce((acc, item) => {
    const key = `${item.userId}-${item.projectId}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  // Calcular variação para cada grupo
  Object.entries(groupedData).forEach(([key, items]) => {
    // Ordenar por data (mais antiga para mais recente)
    const sortedItems = items.filter(item => item.details.Geral > 0).sort((a, b) =>
      moment(a.date, "DD/MM/YYYY").unix() - moment(b.date, "DD/MM/YYYY").unix()
    );

    if (sortedItems.length > 1) {
      const oldest = sortedItems[0].details.Geral;
      const newest = sortedItems[sortedItems.length - 1].details.Geral;

      const variation = ((newest - oldest) / oldest) * 100;
      variationByUserProject[key] = variation.toFixed(2);
    } else {
      // Sem variação se houver apenas um item
      variationByUserProject[key] = 0;
    }
  });

  // Adiciona a variação aos dados finais
  dataToReturn = dataToReturn.map(item => ({
    ...item,
    color: colorsByMonthYear[item?.details.monthYear],
    variation: variationByUserProject[`${item.userId}-${item.projectId}`]
  }));


  // Calcula a variação por usuário e projeto para cada linha com base no item anterior
  // const variationByUserProject = {};

  // // Agrupar por usuário/projeto
  // const groupedData = dataToReturn.reduce((acc, item) => {
  //   const key = `${item.userId}-${item.projectId}`;
  //   if (!acc[key]) acc[key] = [];
  //   acc[key].push(item);
  //   return acc;
  // }, {});

  // // Calcular variação para cada linha do grupo
  // Object.entries(groupedData).forEach(([key, items]) => {
  //   // Ordenar por data (mais antiga para mais recente)
  //   const sortedItems = items
  //     .filter(item => item.details.Geral > 0)
  //     .sort((a, b) =>
  //       moment(a.date, "DD/MM/YYYY").unix() - moment(b.date, "DD/MM/YYYY").unix()
  //     );

  //   // Calcular a variação para cada item, comparando com o item anterior
  //   sortedItems.forEach((item, index) => {
  //     if (index > 0) {
  //       const previousItem = sortedItems[index - 1];
  //       const current = item.details.Geral;
  //       const previous = previousItem.details.Geral;

  //       // Calcula a variação percentual
  //       const variation = ((current - previous) / previous) * 100;

  //       // Armazena a variação para o item atual
  //       variationByUserProject[`${item.userId}-${item.projectId}-${item.date}`] = variation.toFixed(2);
  //     } else {
  //       // Para o primeiro item, não há variação anterior (pode ser 0 ou nulo)
  //       variationByUserProject[`${item.userId}-${item.projectId}-${item.date}`] = 0;
  //     }
  //   });
  // });

  // // Adiciona a variação aos dados finais
  // dataToReturn = dataToReturn.map(item => ({
  //   ...item,
  //   color: colorsByMonthYear[item?.details.monthYear],
  //   variation: variationByUserProject[`${item.userId}-${item.projectId}-${item.date}`]
  // }));

  return { dataToReturn, colorsByMonthYear };
};

const ESMContainer = () => {
  const pageRef = useRef(null);
  const abortControllerRef = useRef(null);
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { scope, scopeType, projectsByScope } = useScope()
  const { esmId } = useParams()
  const [reload, setReload] = useState(false);
  const [loading, setLoading] = useState(false);
  const { users, loaded} = useUsers()
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [modalFilterIsOpen, setModalFilterIsOpen] = useState(false)
  const [selectedsUsers, setSelectedsUsers] = useState([])
  const [selectedUsersProject, setSelectedUsersProject] = useState([])
  const [selectedCity, setSelectedCity] = useState({})
  const [checked, setChecked] = useState([])
  const [saving, setSaving] = useState(false)
  const currentUserRole = useSelector((state) => state.app.currentUserRole)
  const selectESM = useSelector((state) =>
    state.app.selectESM && state.app.selectESM.value
      ? state.app.selectESM
      : { value: 0, label: "Escala" },
  )
  const esmData = useSelector((state) => state.app.esmData)
  const summaryData = useSelector((state) => state.app.summaryData)
  const indicatorsData = useSelector((state) => state.app.indicatorsData)
  const colorsByMonthYear = useSelector((state) => state.app.colorsByMonthYear)
  const history = useSelector((state) => state.app.history)

  const [arrayRecommendations, setArrayRecommendations] = useState([])
  const [esmDetails, setEsmDetails] = useState({});
  const [codesValuesProcessing, setCodesValuesProcessing] = useState([])
  const [isSearch, setIsSearch] = useState(false)
  const [valuesCodeSearch, setValuesCodeSearch] = useState([])
  const [valuesSearch, setResponseCodesSearch] = useState({})
  const [searchTerm, setSearchTerm] = useState("")
  const [allHeaderVariables, setAllHeaderVariables] = useState({});
  const [loadingSearch, setLoadingSearch] = useState(false)
  const [filter, setFilter] = useState({})
  const [dateFilter, setDateFilter] = useState(null)
  const [dateFilterLoading, setDateFilterLoading] = useState(false)
  const [filteredData, setFilteredData] = useState({
    filteredUsers: [],
    filteredHistory: []
  });

  const [selectedEvolution, setSelectedEvolution] = useState([])
  const [resetFields, setResetFields] = useState(false)
  const [categoryIsOpen, setCategoryIsOpen] = useState(false)
  const [checkedCategory, setCheckedCategories] = useState([])

  const {
    register,
    handleSubmit,
    setValue,
    control,
  } = useForm({
    mode: "onChange",
  })

  useEffect(() => {
    if(valuesSearch && valuesSearch.data) {
      setModalFilterIsOpen(false)
    }
  }, [valuesSearch])

  useEffect(() => {
    if(resetFields) {
      setResponseCodesSearch(null);
      setModalFilterIsOpen(false)
      setResetFields(false);
    }
  }, [resetFields])

  useEffect(() => {
    if(scope?.id && projectsByScope) {
      setFilter({...filter, projects: projectsByScope.map(row => ({ value: row.id, label: row?.description }))})
    }
  }, [scope?.id])

  useEffect(() => {
    if(esmId) {
      Send.get(`/scale-indicator/allesm/${scope?.id}/${esmId}`, `scopeType=${scopeType}`).then(dataComplete => {
        console.log({dataComplete})
      })

      Send.get(`/scale/${esmId}`)
      .then(result => {
        if(result?.id) {
          const config = result?.config ? JSON.parse(result?.config) : [];

          setEsmDetails({ ...result, config });
        }
      })
      .catch(error => {
        console.log("Erro ao buscar detalhes do ESM", error);
        customToast("Erro ao buscar detalhes do ESM", 'error');
      }) 
    }
  }, [esmId]);

  const handleProcessUsers = async (value) => {
    let extraQueries = `?end=${value?.end || moment().format('YYYY-MM-DD')}`;

    if (!selectedsUsers.length) {
      console.log("Não há usuários selecionados.")
      return
    }

    if(value?.start) {
      extraQueries += `&start=${value?.start}`;
    }

    console.log({ selectedsUsers })

    const completedSelectedUsers = users.filter(user => selectedsUsers.includes(user.id));

    setSaving(true);

    for await (const projectId of selectedUsersProject) {
      const projectUsers = completedSelectedUsers.filter(row => row.projectId === projectId);

      const projectName = projectUsers[0].project;

      if(projectUsers.length && projectName) {
        customToast(`Gerando dados do projeto: ${projectName}`);
        console.log({
          users: projectUsers.map(row => row.id),
          scaleId: esmId,
          project: projectId,
          code: [],
        })
        await Send.post(`/ia/esm/${extraQueries}`, {
          users: projectUsers.map(row => row.id),
          scaleId: esmId,
          project: projectId,
          code: [],
        }).then((res) => {
          customToast(`Dados carregados com sucesso: ${projectName}`, 'success');
        })
        .catch(error => {
          console.log(`Erro ao carregar dados: ${projectName}`, error);
          customToast(`Erro ao carregar dados: ${projectName}`, 'error');
        })
      }
    }

    setReload(true);
    handleCloseModal();
    setSaving(false);
  }

  const loadEsmData = async () => {
    // Cancela a requisição anterior se existir
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Cria um novo AbortController para esta requisição
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      if(!Array.isArray(esmData) || !esmData.length) setLoading(true)
      
      // Se há filtro de data, ativa o loading específico do filtro de data
      if (dateFilter) {
        setDateFilterLoading(true)
      }

      let queryParams = `scopeType=${scopeType}`;
      
      // Se o filtro de data estiver definido, adiciona a data como parâmetro
      if (dateFilter) {
        const formattedDate = moment(dateFilter, 'DD/MM/YYYY').format('YYYY-MM-DD');
        queryParams += `&date=${formattedDate}`;
        console.log('Carregando dados com data:', formattedDate, 'Query:', queryParams)
      }

      let esm = await Send.get(`/scale-indicator/allesm/${scope?.id}/${esmId}`, queryParams, true, { signal })
    // esm = esm.filter(item => item.generalScore > 0)
    
    const history = [];
    
    const { colorsByMonthYear, dataToReturn } = formatEsmData(esm, users);

    const monthlyAverages = calculateAccumulatedAverages(dataToReturn, "summary");
    const monthlyAveragesIndicators = calculateAccumulatedAverages(dataToReturn, "fields");

    // Ordena por data (mais recente primeiro)
    const sortedData = dataToReturn.sort((a, b) =>
      moment(a.date, "DD/MM/YYYY").unix() - moment(b.date, "DD/MM/YYYY").unix()
    );

    // Filtra usuários únicos, mantendo o item mais recente
    const uniqueUsers = Array.from(
      new Map(sortedData.map(item => [item.userId, item])).values()
    );

    console.log({dataToReturn, monthlyAverages})

    dispatch(
      setData({
        colorsByMonthYear,
        esmData: uniqueUsers,
        summaryData: monthlyAverages,
        indicatorsData: monthlyAveragesIndicators,
        history: history.length && history.sort((a,b) => {
          const summaryA = JSON.parse(a.summary);
          const summaryB = JSON.parse(b.summary);

          return  (summaryB['Químico'] + summaryB['Biológico']) - (summaryA['Químico'] + summaryA['Biológico']);
        }).map(row => {

          return { ...row, user: users.length ? users.find(user => user.id === row.userId ) : {} }
        }) || [],
      }),
    )

      // setLoading(false)
      setLoading(false)
      setDateFilterLoading(false)
    } catch (error) {
      // Se a requisição foi cancelada, não fazer nada
      if (error.name === 'AbortError') {
        console.log('Requisição cancelada:', error.message);
        return;
      }
      
      // Para outros erros, definir loading como false e mostrar erro
      console.error('Erro ao carregar dados ESM:', error);
      setLoading(false)
      setDateFilterLoading(false)
    }
  }

  const getAllHeaderVariables = (fields) => {
    const formatFields = fields;
    const newHeaderVariables = {};

    formatFields.map(field => {
      const variables = isJson(field.variables) && JSON.parse(field.variables); 
      newHeaderVariables[field?.title] = variables;
    })

    setAllHeaderVariables(newHeaderVariables);
  }

  useEffect(() => {
    if(esmId || reload){
      loadEsmData()
      setReload(false)
    }

    // Cleanup: cancela requisições pendentes quando componente desmonta ou dependências mudam
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }
  }, [esmId, reload, scope, dateFilter])

  useEffect(() => {
    if(valuesCodeSearch?.some(item => item === "")){
      setIsSearch(false)
    }
  }, [valuesCodeSearch])

  const changeOptions = (selecteds) => {
    const newSelectedProjects = [...new Set([
      ...users.filter(user => selecteds.includes(user.id)).map(row => row.projectId)
    ])];
    console.log("newSelectedProjects", newSelectedProjects)

    setSelectedUsersProject(newSelectedProjects);
    setSelectedsUsers(selecteds);
  }

  const goTo = (data) => {
    navigate(`/esm/${esmId}/producer/${data.value}`, {
      state: {
        esm: data.item,
        esmId,
        producer: data.value,
        hiddenHeader: true,
        scaleProducerId: data.item?.scaleProducerId || data.scaleProducerId
      }
    });
  }

  const goToEsm = () => {
    navigate(
      `/esm/${esmId}/community`,
      { state: {
        esmId: esmId,
        hiddenHeader: true,
        esmData: filterEsmData()
      } });
  }

  const goToUser = (row) => {
    if(row?.value) {
      window.open(`https://conexao.impactools.social/farmer/profile/${row.value}`, "_blank")
    }
  }

  const openModal = () => {
    setModalIsOpen(true)
  }

  const handleCloseModal = () => {
    setModalIsOpen(false)
    setSelectedsUsers([]);
    setValue("start", "")
    setValue("end", "")
    //setCodesValuesProcessing([])
    //setIsSearch(false)
  }

  const onDeleteUser = async (id) => {
    const res = await Send.delete(`/scale-producer/${id}`);
    if(res){
      setReload(true)
    }
  }

  const handleChange = (value) => {
    setSelectedCity(value)
  }

const esmUsersList = Array.isArray(esmData)
  ? esmData
      .map((item) => {
        const user = Array.isArray(users) && users.find((u) => u?.id === item?.userId);
        const codes = (item?.codes && isJson(item?.codes)) ? JSON.parse(item?.codes) : [];

        if (!user) return null;

        return {
          codes: Array.isArray(codes) ? codes.map((code) => code.value).join(', ') : '',
          value: user.id,
          image: user.image,
          label: user.name,
          email: user.email,
          city: user.city,
          item,
          year: item.year,
          data: item.date,
          esmId: esmId,
          pontuation: item.details?.Geral || 0,
          conceito: item.details?.Conceito,
          variation: item.variation,
          projectId: item.projectId,
          badge: item.details?.Conceito && (
            <BadgeEsm label={item.details?.Conceito} type={getType(item.details?.Conceito)} esmId={esmId}/>
          ),
        };
      })
      .filter((item) => item !== null)
  : [];

  const onSearchContent = (e) => {
    setSearchTerm(e.target.value)
  }
  
  useEffect(() => {
    if(esmData && (esmUsersList || history)) {
      console.log({esmData, esmUsersList, history})
      const result = applyFilterEsm(sort(esmUsersList).desc(u => u.pontuation), history, valuesSearch, filter, selectedEvolution)
      console.log({result})
      getAllHeaderVariables(esmData[0]?.fields || [])
      setFilteredData(result);
    }

    return () => setFilteredData({
      filteredUsers: [],
      filteredHistory: []
    })
  }, [valuesSearch, filter, esmData])
  
  const filterEsmData = () => {
    return filteredData?.filteredUsers.length > 0
      ? esmData
          ?.filter((item) => filteredData?.filteredUsers.some((a) => a?.item?.scaleProducerId === item?.scaleProducerId))
      : [];
  };

  const handleClick = () => {
    setModalFilterIsOpen(true)
  }

  const sendValues = () => {
    setIsSearch(true)
    setModalFilterIsOpen(false)
  }

  const onChangeFilter = (field, value) => {
    return (value) => {
      setFilter((values) => ({ ...values, [field]: value }))
    }
  }

  const onChangeFilterSearch = (field, value) => {
    if(value){
      setFilter((values) => ({ ...values, [field]: value }))
    } else {
      setFilter({search: ""})
    }
  }

  const onClearLocalFilters = () => {
    // Limpa todos os filtros locais quando filtro de ano via códigos é aplicado
    setFilter({})
  }

  const onDateChange = (dateString, date) => {
    console.log('Data selecionada:', dateString, date)
    setDateFilter(dateString)
  }

  const scrollToTopOrBottom = () => {
    pageRef.current.scrollIntoView({ behavior: 'smooth', top: 0 })
  }

  const categoryCloseModal = () => {
    setCategoryIsOpen(!categoryIsOpen)
  }

  const categories = {
    Quimico: 
      [
        {
          value: "pH"
        },
        {
          value: "m (%)"
        },
        {
          value: "Zn (mg/dm3)"
        },
        {
          value: "V (%)"
        },
        {
          value: "S (mg/dm3)"
        },
        {
          value: "P (mg/dm3)"
        },
        {
          value: "Mn (mg/dm3)"
        },
        {
          value: "MO (dag/kg ou %)"
        },
        {
          value: "Cu (mg/dm3)"
        },
        {
          value: "B (mg/dm3)"
        },
      ]
    ,
    Biologico : 
      [
        {
          value: "IQS_tax"
        },
        {
          value: "IQS_Ec"
        },
        {
          value: "GF_total"
        }
      ]
    
  };
  

  useEffect(() => {
    const allValues = Object.values(categories).flatMap((category) =>
      category.map((item) => item.value),
    )
    setCheckedCategories(allValues)
  }, [])

  const handleChangeCategories = (e) => {
    const { checked, value } = e.target
    setCheckedCategories((prev) => (checked ? [...prev, value] : prev.filter((val) => val !== value)))
  }

  const exportData = async () => {
    let queryParams = `scopeType=${scopeType}`;
    
    // Se o filtro de data estiver definido, adiciona a data como parâmetro
    if (dateFilter) {
      const formattedDate = moment(dateFilter, 'DD/MM/YYYY').format('YYYY-MM-DD');
      queryParams += `&date=${formattedDate}`;
    }

    let esm = await Send.get(`/scale-indicator/allesm/${scope?.id}/${esmId}`, queryParams)
    esm = esm.filter(item => item.generalScore > 0)

    const { dataToReturn } = formatEsmData(esm, users);

    let formattedToExport = dataToReturn.map(item => {
      const data = {
        IDProdutor: item.user[0].id,
        Produtor: item.user[0].name,
        Data: item.date,
        Geral: item.details.Geral,
        ...item.summary,
      }
      item.fields.map(item => {
        data[`${item.category} - ${item.title}`] = item.value
      })
      return data;
    })

    // console.log({formattedToExport})
    await jsonToExcel("Dados ESM", formattedToExport)
  }

  return esmDetails?.id ? (
    <>
      <PageHeaderAddon >
      {(currentUserRole === 'USER_ADMIN' || currentUserRole === 'USER_MANAGER')  ? <div className="esm-header-button">
          {/* <Button label="Carregar dados" handleClick={openModal} variant="outline" /> */}
          <Button label="Exportar" handleClick={exportData} variant="outline" />
        </div>
        : <></>
      }
      </PageHeaderAddon>
      <ESMScreen
        data={filterEsmData()}
        esmUsersList={filteredData?.filteredUsers || []}
        loading={loading}
        newArray={arrayESM}
        {...{
          pageRef,
          history: filteredData?.filteredHistory || [],
          saving,
          handleProcessUsers: handleSubmit(handleProcessUsers),
          handleCloseModal,
          goToEsm,
          goTo,
          arrayRecommendations,
          changeOptions,
          setSelectedsUsers,
          selectedsUsers,
          setModalIsOpen,
          modalIsOpen,
          users,
          selectESM,
          checked,
          esmId,
          register,
          onDeleteUser,
          goToUser,
          isDeletable: currentUserRole === 'USER_ADMIN',
          colorsByMonthYear,
          setModalFilterIsOpen,
          modalFilterIsOpen,
          handleChange,
          selectedCity,
          setCodesValuesProcessing,
          onSearchContent,
          handleClick,
          sendValues,
          setValuesCodeSearch,
          setResponseCodesSearch,
          valuesCodeSearch,
          isSearch,
          setIsSearch,
          setLoadingSearch,
          loadingSearch,
          control, 
          onChangeFilter,
          onChangeFilterSearch,
          layoutConfig: esmDetails?.config,
          setSelectedEvolution, 
          evolutionFilterTree,
          scrollToTopOrBottom,
          resetFields,
          setResetFields,
          categoryIsOpen,
          categoryCloseModal,
          checkedCategory,
          categories,
          handleChangeCategories,
          allHeaderVariables,
          projectsByScope,
          filter,
          summaryData,
          indicatorsData,
          onClearLocalFilters,
          onDateChange,
          dateFilterLoading
        }}
      />
    </>
  ) : <></>
}

export default ESMContainer

export const arrayESM = [
  { id: 0, label: "Sustentável", color: "#6aaa73", value: 18 },
  { id: 1, label: "Quase Sustentável", color: "#d5e370", value: 36 },
  { id: 2, label: "Intermediário", color: "#f7c648", value: 54 },
  { id: 3, label: "Quase Insustentável", color: "#ef825d", value: 72 },
  { id: 4, label: "Insustentável", color: "#c65c5e", value: 90 },
]
